/**
 * fetch() for this app's own API, with an ID token that is never stale.
 *
 * Every API call sends the signed-in user's ID token in X-Id-Token (see TOKEN_HEADER).
 * That token lives about an hour, and a tab is often open far longer, so it cannot be
 * read once at sign-in and reused: every call after the hour 401s with "Invalid token".
 *
 * Callers still build the header from `useAuth().token` exactly as before — that marks
 * the request as authenticated. apiFetch swaps in the current token on the way out:
 *
 *   - the cached token, when it has more than RENEW_BEFORE_S left (no network);
 *   - otherwise a fresh one from MSAL (one refresh-token call, shared by every request
 *     that arrives while it is in flight);
 *   - and on a 401 regardless, one forced refresh and one retry, for a token revoked or
 *     clock-skewed past the expiry check.
 *
 * WHY THE EXPIRY CHECK IS OURS, NOT MSAL'S: acquireTokenSilent judges freshness by the
 * ACCESS token. The access token (60–90 min) can outlive the ID token (60 min), and then
 * MSAL returns the cached ID token already expired. Only forceRefresh guarantees a new one.
 *
 * WHY useAuth().token IS NOT UPDATED: a dozen effects (the dataset, comments, detail
 * rows) are keyed on it, and changing it would refetch all of them every hour.
 */

/**
 * Azure Static Web Apps overwrites `Authorization` before the request reaches the
 * backend, so the token travels in its own header (api/shared/auth.ts).
 */
export const TOKEN_HEADER = 'X-Id-Token'

/** Renew this long before `exp`: covers clock skew and a slow request. */
export const RENEW_BEFORE_S = 5 * 60

/** Returns the current ID token; `force` skips the cache. Null when none can be had. */
export type IdTokenSource = (force: boolean) => Promise<string | null>

let source: IdTokenSource | null = null

/** AuthProvider registers the signed-in user's source; null on sign-out. */
export function setIdTokenSource(s: IdTokenSource | null): void {
  source = s
}

/** The token's `exp` claim in epoch seconds, or null if it cannot be read. */
export function tokenExpiry(token: string): number | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='))) as {
      exp?: unknown
    }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

/**
 * A source that serves `initial` until it is within RENEW_BEFORE_S of expiring, then
 * calls `refresh`. Concurrent callers share one refresh. A token whose expiry cannot be
 * read is treated as expired. A failed refresh yields the last token held, so the
 * request still goes out and the API's 401 reaches the caller as it always did.
 */
export function createIdTokenSource(
  initial: string | null,
  refresh: () => Promise<string | null>,
  now: () => number = () => Date.now() / 1000,
): IdTokenSource {
  let current = initial
  let inflight: Promise<string | null> | null = null
  return (force) => {
    if (!force && current) {
      const exp = tokenExpiry(current)
      if (exp !== null && exp - now() > RENEW_BEFORE_S) return Promise.resolve(current)
    }
    inflight ??= refresh()
      .then((t) => (current = t ?? current))
      .catch(() => current)
      .finally(() => {
        inflight = null
      })
    return inflight
  }
}

/** fetch(), with X-Id-Token (when the caller set one) replaced by the current token. */
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const s = source
  if (!s || !new Headers(init.headers).has(TOKEN_HEADER)) return fetch(input, init)

  const send = async (force: boolean) => {
    const headers = new Headers(init.headers)
    const token = await s(force)
    if (token) headers.set(TOKEN_HEADER, token)
    return fetch(input, { ...init, headers })
  }
  const res = await send(false)
  return res.status === 401 ? send(true) : res
}
